/**
 * @file grant.schema.ts
 * @description Concessões entre marcas — a governança do ecossistema.
 *
 * Uma concessão responde: "a marca A dá à marca B acesso a quê?". É o que
 * substitui os privilégios implícitos que existiam antes:
 *
 *   - Acesso ao acervo da Eva era **default-on**: todo parceiro via o conteúdo
 *     da Eva, e o que existia era curadoria por cima (`hiddenEvaVideoIds`).
 *     O mantenedor não decidia nada; só dava para esconder na unha.
 *   - Acesso à rede da Eva era a flag `EVA_PROVIDERS` no documento do parceiro
 *     — quer dizer, o acesso ao acervo de uma marca era governado por um campo
 *     da OUTRA marca.
 *   - Compartilhamento entre parceiros era `courseShares`, que já tinha a forma
 *     certa (origem, destino, escopo, revogação) mas só valia para vídeo.
 *
 * Aqui os três viram o mesmo objeto, com um lugar só para conceder e revogar.
 *
 * ## Por que revogar é escrita, não apagamento
 *
 * `status: 'REVOKED'` em vez de deletar o documento. É a regra de
 * reversibilidade do AGENTS.md: reverter uma revogação indevida é limpar um
 * campo, não restaurar um backup. E a concessão revogada continua sendo a
 * resposta para "por que esta marca via este conteúdo em março?".
 */

import { z } from 'zod';
import { brandIdField } from './brand';

/**
 * O que está sendo concedido.
 *
 * Deliberadamente grosso: a granularidade fina de "quais itens" é o `scope`.
 * Um `kind` por item concedível produziria um enum que cresce a cada feature.
 */
export const GrantKindEnum = z.enum([
  /** Acervo de conteúdo: vídeos, revistas e trilhas da marca de origem. */
  'CONTENT_CATALOG',
  /** Rede de profissionais: os profissionais da origem atendem pacientes do destino. */
  'PROVIDER_NETWORK',
  /**
   * Jornadas de cuidado — checkpoints e lembretes da origem valem para as
   * pacientes do destino (fase 4 do plano "A Eva deixa de ser especial").
   * `scope` `SELECTED` lista ids de `careItems`. Por referência: edição na
   * origem propaga; o destino pode ocultar ou duplicar para a própria marca.
   */
  'CARE_JOURNEYS',
  /**
   * Relacionamento (A062, fase D): o time de CRM da origem atende as
   * pacientes do destino — conversa com elas e vê a ficha delas. É como a
   * marca corporativa, sem equipe própria, é atendida pela Eva Maternidade.
   * Mesma direção da rede: a origem atende, o destino é atendido. Não tem
   * itens: `scope` é sempre `ALL`.
   */
  'RELATIONSHIP',
]);
export type GrantKind = z.infer<typeof GrantKindEnum>;

export const GrantStatusEnum = z.enum(['ACTIVE', 'REVOKED']);
export type GrantStatus = z.infer<typeof GrantStatusEnum>;

/**
 * Recorte do que a concessão alcança.
 *
 * `ALL` é o acervo inteiro, inclusive o que a origem publicar depois — é o que
 * reproduz o comportamento histórico do acervo da Eva. `SELECTED` congela uma
 * lista, que é o que `courseShares` fazia.
 */
export const GrantScopeSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('ALL') }),
  z.object({
    mode: z.literal('SELECTED'),
    /**
     * Ids do que foi concedido. Para `CONTENT_CATALOG` são ids de vídeo,
     * revista ou trilha; para `PROVIDER_NETWORK`, ids de `MedicalProvider`.
     */
    itemIds: z.array(z.string()).default([]),
  }),
]);
export type GrantScope = z.infer<typeof GrantScopeSchema>;

export const GrantSchema = z.object({
  id: z.string(),

  /** Marca dona do acervo. */
  sourceBrandId: brandIdField(),
  /** Marca que recebe o acesso. */
  targetBrandId: brandIdField(),

  kind: GrantKindEnum,
  scope: GrantScopeSchema.default({ mode: 'ALL' }),
  status: GrantStatusEnum.default('ACTIVE'),

  /**
   * Código curto de compartilhamento, quando a concessão nasceu de um convite.
   *
   * Herdado de `courseShares`, onde o parceiro de destino colava o código para
   * importar. Concessão criada pelo mantenedor não tem código — o campo
   * distingue as duas origens sem precisar de um enum extra.
   */
  shareCode: z.string().optional(),

  /**
   * Quem criou. Guardado porque concessão é decisão de governança, e a próxima
   * pessoa vai perguntar quem autorizou.
   */
  createdByUserId: z.string().optional(),

  /** Nota livre do mantenedor: o "por quê" da concessão. */
  note: z.string().optional(),

  revokedAt: z.coerce.date().nullable().default(null),
  revokedByUserId: z.string().optional(),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Grant = z.infer<typeof GrantSchema>;

/**
 * A concessão está valendo agora?
 *
 * Existe como função (e não como comparação solta) porque "ativa" já significa
 * duas coisas — status e ausência de revogação — e vai significar mais quando
 * houver validade por data.
 */
export function isGrantActive(grant: Pick<Grant, 'status' | 'revokedAt'>): boolean {
  return grant.status === 'ACTIVE' && !grant.revokedAt;
}

/**
 * A concessão alcança este item?
 *
 * Concessão de escopo `ALL` alcança qualquer item, inclusive os que a origem
 * criar depois — é o que faz o acervo da Eva continuar aparecendo sem que
 * ninguém precise reconceder a cada vídeo novo.
 */
export function grantCoversItem(grant: Pick<Grant, 'scope'>, itemId: string): boolean {
  if (grant.scope.mode === 'ALL') return true;
  return grant.scope.itemIds.includes(itemId);
}
